import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiAlertCircle, FiCheckCircle, FiSave } from 'react-icons/fi';
import { supabase, TABLES } from '../../supabase/supabase';

const RecruitmentTargetSection = ({ question, targetN, projectId }) => {
  const [targets, setTargets] = useState({});
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (question.id && question.options?.length > 0) {
      fetchExistingTargets();
    }
  }, [question.id, question.options]);

  const fetchExistingTargets = async () => {
    try {
      const { data, error } = await supabase
        .from(TABLES.SCREENER_QUESTION_TARGETS)
        .select('*')
        .eq('question_id', question.id);
      
      if (error) throw error;

      if (data && data.length > 0) {
        const targetMap = {};
        data.forEach(t => {
          targetMap[t.category_value] = t.target_percentage;
        });
        setTargets(targetMap);
      } else {
        const options = question.options;
        const count = options.length;
        if (count > 0) {
          const base = Math.floor(100 / count);
          const remainder = 100 % count;
          const initialTargets = {};
          options.forEach((opt, idx) => {
            initialTargets[opt.value] = idx === count - 1 ? base + remainder : base;
          });
          setTargets(initialTargets);
        }
      }
    } catch (err) {
      console.error("Error fetching targets:", err);
    }
  };

  const handlePercentageChange = (val, category) => {
    setTargets({ ...targets, [category]: parseFloat(val) || 0 });
    setSaved(false);
  };

  const totalPercentage = Object.values(targets).reduce((sum, val) => sum + val, 0);
  const isComplete = Math.abs(totalPercentage - 100) < 0.01;
  const isOver = totalPercentage > 100;

  const handleSave = async () => {
    if (!projectId || !question.id) return;
    const upsertData = question.options.map(opt => ({
      project_id: projectId,
      question_id: question.id,
      category_value: opt.value,
      target_percentage: targets[opt.value] || 0,
      target_count: Math.round(targetN * ((targets[opt.value] || 0) / 100))
    }));

    setLoading(true);
    try {
      const { error } = await supabase
        .from(TABLES.SCREENER_QUESTION_TARGETS)
        .upsert(upsertData, { onConflict: 'project_id,question_id,category_value' });
      if (error) throw error;
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert(`Failed to save targets: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!targetN || targetN === 0) {
    return (
      <div className="mt-4 p-4 bg-amber-50 rounded-md border border-amber-100">
        <p className="text-xs text-amber-700 font-bold">
          Set your total interview count when creating or editing the project first
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 pt-6 border-t border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Recruitment targets</h4>
        <div className="flex items-center gap-3">
          <span className={`text-[10px] font-black uppercase ${
            isComplete ? 'text-emerald-500' : isOver ? 'text-amber-500' : 'text-gray-400'
          }`}>
            {totalPercentage}% allocated
          </span>
        </div>
      </div>

      <div className="space-y-4 mb-8">
        {question.options.map((opt) => (
          <div key={opt.value} className="flex items-center justify-between group">
            <span className="text-xs font-bold text-gray-600">{opt.label}</span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="w-16 px-2 py-1 text-xs font-bold border border-gray-200 rounded-md focus:border-rutabaga-pink outline-none"
                  value={targets[opt.value] ?? ''}
                  placeholder="0"
                  onChange={(e) => handlePercentageChange(e.target.value, opt.value)}
                />
                <span className="text-xs font-bold text-gray-400">%</span>
              </div>
              <span className="text-[10px] font-bold text-gray-400 min-w-[80px]">
                = {Math.round(targetN * ((targets[opt.value] || 0) / 100))} participants
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-50">
        <div className="flex-1">
          {!isComplete && (
            <div className="flex items-center gap-2 text-amber-500">
              <SafeIcon icon={FiAlertCircle} className="text-sm" />
              <p className="text-[10px] font-bold uppercase">Targets must add up to 100%</p>
            </div>
          )}
        </div>
        <button 
          onClick={handleSave}
          disabled={loading}
          className="rutabaga-button-primary py-2 px-6 flex items-center gap-2 text-xs"
        >
          <SafeIcon icon={saved ? FiCheckCircle : FiSave} />
          <span>{saved ? 'Saved' : 'Save Targets'}</span>
        </button>
      </div>
    </div>
  );
};

export default RecruitmentTargetSection;